import type { WebsiteMediaKind } from '../../types/website-templates';

/**
 * A5 platform media limits. There is no transcoding; MOV/HEVC/AV1 are
 * rejected. Mirrored by the `storefront-media` Edge validators and SQL
 * renderability rules.
 */
const MIB = 1024 * 1024;

export const WEBSITE_MEDIA_LIMITS = {
  image: {
    mimeTypes: ['image/jpeg', 'image/png', 'image/webp'],
    maxBytes: 5 * MIB,
    minDimensionPx: 1,
    maxDimensionPx: 4096,
  },
  gif: {
    mimeTypes: ['image/gif'],
    maxBytes: 8 * MIB,
    minDimensionPx: 1,
    maxDimensionPx: 1600,
    maxFrames: 300,
  },
  video: {
    mimeTypes: ['video/mp4', 'video/webm'],
    maxBytes: 20 * MIB,
    minDimensionPx: 1,
    maxDimensionPx: 1920,
    maxDurationMs: 15_000,
    codecs: {
      'video/mp4': { video: ['avc1'], audio: ['mp4a'] },
      'video/webm': { video: ['V_VP8', 'V_VP9'], audio: ['A_OPUS', 'A_VORBIS'] },
    },
  },
} as const;

export const REJECTED_VIDEO_CONTAINERS = ['video/quicktime'] as const;

export const WEBSITE_MEDIA_ALT_MAX_LENGTH = 200;

export type WebsiteMediaMimeType =
  (typeof WEBSITE_MEDIA_LIMITS)[WebsiteMediaKind]['mimeTypes'][number];

export function websiteMediaKindForMime(mimeType: string): WebsiteMediaKind | null {
  const normalized = mimeType.trim().toLowerCase();
  for (const kind of ['image', 'gif', 'video'] as const) {
    if ((WEBSITE_MEDIA_LIMITS[kind].mimeTypes as readonly string[]).includes(normalized)) {
      return kind;
    }
  }
  return null;
}

export function isRejectedVideoContainer(mimeType: string): boolean {
  return (REJECTED_VIDEO_CONTAINERS as readonly string[]).includes(mimeType.trim().toLowerCase());
}

/** `accept` attribute for a slot's allowed kinds, in stable kind order. */
export function acceptAttributeFor(kinds: readonly WebsiteMediaKind[]): string {
  const mimeTypes: string[] = [];
  for (const kind of ['image', 'gif', 'video'] as const) {
    if (!kinds.includes(kind)) continue;
    mimeTypes.push(...WEBSITE_MEDIA_LIMITS[kind].mimeTypes);
  }
  return mimeTypes.join(',');
}
